import {
	BasicButton,
	CreatePasswordValidation,
	SecondCustomInput,
} from "@/components";
import env from "@/env.config";
import useProfile from "@/hooks/useProfile";
import useRegisterMember from "@/hooks/useRegisterMember";
import useUpdateMember from "@/hooks/useUpdateMember";
import { STORAGE_KEYS } from "@/libraries/constant";
import { isAxiosError } from "@/libraries/helper";
import { AuthRegisterCredentialsSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/create-account/")({
	component: RouteComponent,
});

function RouteComponent() {
	const [passwordVisible, setPasswordVisible] = useState(false);
	const [confirmationVisible, setConfirmationVisible] = useState(false);
	const turnstileEnabled = env.turnstileEnabled;
	const [turnstileToken, setTurnstileToken] = useState("");
	const turnstileRef = useRef<TurnstileInstance>(null);

	const resetTurnstile = () => {
		turnstileRef.current?.reset();
		setTurnstileToken("");
	};

	const navigate = Route.useNavigate();
	const { profile } = useProfile();
	const { mutateAsync: mutateAsyncRegisterUser } = useRegisterMember();
	const { mutateAsync: mutateAsyncUpdateUser } = useUpdateMember();

	const {
		control,
		handleSubmit,
		watch,
		reset,
		setError,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<AuthRegisterCredentialsSchema>({
		mode: "all",
		resolver: zodResolver(AuthRegisterCredentialsSchema),
		defaultValues: {
			email: "",
			password: "",
			password_confirmation: "",
		},
	});

	const onSubmit: SubmitHandler<AuthRegisterCredentialsSchema> = async (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		if (profile?.id) {
			return mutateAsyncUpdateUser(
				{
					id: profile.id,
					...data,
				},
				{
					onSuccess: () => {
						localStorage.setItem(
							STORAGE_KEYS.FORM_CREDENTIALS,
							JSON.stringify(data),
						);
						return navigate({
							to: "/create-account/verify-mobile",
						});
					},
					onError: (err) => {
						if (isAxiosError(err, 422)) {
							const message =
								// @ts-expect-error
								err?.response?.data?.messages?.[0] ??
								"This email is already registered";
							return setError("email", {
								message,
								type: "validate",
							});
						}
						return toast.error("Failed to update account", {
							position: "top-center",
						});
					},
				},
			);
		}
		return mutateAsyncRegisterUser(
			{ ...data, cf_turnstile_token: turnstileToken },
			{
				onSuccess: () => {
					localStorage.setItem(
						STORAGE_KEYS.FORM_CREDENTIALS,
						JSON.stringify(data),
					);
					return navigate({
						to: "/create-account/verify-mobile",
					});
				},
				onError: (err) => {
					// Turnstile tokens are single-use; reset the widget so the user can retry.
					resetTurnstile();
					if (isAxiosError(err, 422)) {
						// @ts-expect-error
						const captchaError = err?.response?.data?.errors as
							| string[]
							| undefined;
						if (Array.isArray(captchaError) && captchaError.length > 0) {
							return toast.error(captchaError.join(", "), {
								position: "top-center",
							});
						}
						const message =
							// @ts-expect-error
							err?.response?.data?.messages?.full_messages?.join(", ");
						return setError("email", {
							message,
							type: "validate",
						});
					}
					return toast.error("Failed to create account", {
						position: "top-center",
					});
				},
			},
		);
	};

	useEffect(() => {
		const formCredentials = localStorage.getItem(STORAGE_KEYS.FORM_CREDENTIALS);
		if (formCredentials) {
			reset(JSON.parse(formCredentials), {
				keepDefaultValues: true,
			});
		}
	}, [reset]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="relative flex-1 flex flex-col overflow-hidden"
		>
			<div className="container space-y-6 pt-4 flex-1 overflow-y-auto">
				<Controller
					control={control}
					name="email"
					defaultValue=""
					render={({ field, fieldState }) => (
						<SecondCustomInput
							type="email"
							label="Email"
							name={field.name}
							value={field.value}
							onChange={field.onChange}
							onBlur={field.onBlur}
							isShowErrorMessage
							errorMessage={fieldState.error?.message}
							startIcon={<MailOutlineIcon />}
						/>
					)}
				/>
				<Controller
					control={control}
					name="password"
					defaultValue=""
					render={({ field, fieldState }) => (
						<SecondCustomInput
							type="password"
							label="Password"
							name={field.name}
							value={field.value}
							onChange={field.onChange}
							onBlur={field.onBlur}
							errorMessage={fieldState.error?.message}
							onToggleVisibility={() => setPasswordVisible(!passwordVisible)}
							showPassword={passwordVisible}
							startIcon={<LockOutlinedIcon />}
						/>
					)}
				/>
				<CreatePasswordValidation value={watch("password")} />
				<Controller
					control={control}
					name="password_confirmation"
					defaultValue=""
					render={({ field, fieldState }) => (
						<SecondCustomInput
							type="password"
							label="Confirm"
							name={field.name}
							value={field.value}
							onChange={field.onChange}
							onBlur={field.onBlur}
							errorMessage={fieldState.error?.message}
							onToggleVisibility={() =>
								setConfirmationVisible(!confirmationVisible)
							}
							showPassword={confirmationVisible}
							startIcon={<LockOutlinedIcon />}
						/>
					)}
				/>
				{turnstileEnabled && (
					<Turnstile
						ref={turnstileRef}
						siteKey={env.turnstileSiteKey}
						onSuccess={setTurnstileToken}
						onExpire={() => setTurnstileToken("")}
						onError={() => setTurnstileToken("")}
						options={{ size: "flexible" }}
					/>
				)}
			</div>
			<div className="container py-4 shadow">
				<BasicButton
					type="submit"
					variant="contained"
					color="secondary"
					disabled={
						!isDirty || !isValid || (turnstileEnabled && !turnstileToken)
					}
					loading={isSubmitting}
					className="w-full !h-12 text-sm font-semibold !shadow-none"
				>
					Next
				</BasicButton>
			</div>
		</form>
	);
}
